/**
 * THE PUBLIC DEMO'S ADDRESS — prest0.ai/demo/<industry>/<any app route> (exception M1 in CLAUDE.md).
 *
 * The same app and the same build as prest0.ai, with the router based at `/demo/<slug>` (the way the new design's
 * tree is based at `/prest0design`, src/design/previewPath.ts), and every backend answered by a script inside the
 * browser. Bare `/demo` is not in the tree: it stays the booking page.
 *
 * Which industries answer is a build lever, `REACT_APP_DEMO_INDUSTRIES`: a CSV of slugs where `*` means every one
 * (CLAUDE.md client levers; the code default is off, so an unset build has no demo at all).
 *
 * THIS MODULE IS ON THE FIRST DOWNLOAD and is read before anything else on the page (src/demo/demoBoot.ts): keep it
 * to string tests. Everything the demo draws or runs lives in its own on-demand chunks.
 */
import { buildEnv } from '../config/buildEnv';
import { DEMO_INDUSTRY_IDS, type DemoIndustryId } from './demoIndustries';

const currentPath = (): string =>
  typeof window !== 'undefined' && window.location ? window.location.pathname || '/' : '/';

/** Whether the build lets `slug` answer (the lever's CSV, `*` for every industry). */
export function demoLeverOn(slug: string, csv: string | null | undefined = buildEnv().REACT_APP_DEMO_INDUSTRIES): boolean {
  const listed = String(csv ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return listed.includes('*') || listed.includes(slug);
}

/**
 * The industry whose demo `pathname` is in, or null. Case-insensitive, and the slug must end at a "/" or at the end of
 * the path, mirroring react-router's own basename rule — /demo/lawyers is not the law demo, /DEMO/Law/wallet is.
 */
export function demoIndustryOf(
  pathname: string = currentPath(),
  csv?: string | null,
): DemoIndustryId | null {
  const lower = pathname.toLowerCase();
  if (!lower.startsWith('/demo/')) return null;
  const rest = lower.slice('/demo/'.length);
  const end = rest.indexOf('/');
  const slug = end === -1 ? rest : rest.slice(0, end);
  if (!(DEMO_INDUSTRY_IDS as readonly string[]).includes(slug)) return null;
  if (!demoLeverOn(slug, csv === undefined ? buildEnv().REACT_APP_DEMO_INDUSTRIES : csv)) return null;
  return slug as DemoIndustryId;
}

/**
 * The industries the lever lets in, in their order: what the site's header and footer offer (no industry, no entry).
 * Called while a page draws, so it reads the build's own lever unless a test hands it one.
 */
export function demoChoices(csv: string | null | undefined = buildEnv().REACT_APP_DEMO_INDUSTRIES): DemoIndustryId[] {
  return DEMO_INDUSTRY_IDS.filter((id) => demoLeverOn(id, csv));
}

/** Where a choice goes: the industry's demo address, opened with a full page load (the demo boots on load). */
export function demoAddressOf(industry: DemoIndustryId): string {
  return `/demo/${industry}`;
}

/** The tree's prefix when `pathname` is inside a demo, else ''. */
export function demoTreeBase(pathname: string = currentPath(), csv?: string | null): string {
  const industry = demoIndustryOf(pathname, csv);
  return industry ? `/demo/${industry}` : '';
}

let decided: { industry: DemoIndustryId | null } | null = null;

/**
 * The industry this page is a demo of, or null — decided once, from the address the page loaded at. A page never
 * enters or leaves the demo without a full page load, so the answer holds for the life of the page.
 */
export function demoIndustry(): DemoIndustryId | null {
  if (!decided) decided = { industry: demoIndustryOf() };
  return decided.industry;
}

/** True on a page that loaded inside a demo. */
export function isDemoPage(): boolean {
  return demoIndustry() !== null;
}

/** Tests only: forget the page's decision. */
export function resetDemoDecisionForTests(): void {
  decided = null;
}
